import { useMemo } from 'react';

import { useProject } from '@contexts/Project.context';
import { useObjects } from '@hooks/useObjects';

// The first node with a model in the order the tree shows them (depth-first).
const firstAvailableIn = (tree = []) => {
	for (const node of tree) {
		if (node.is_available) return node;
		const below = firstAvailableIn(node.children);
		if (below) return below;
	}
	return null;
};

/**
 * The taxonomy object annotation mode draws boxes for: the one picked in the
 * project context, else the default — the first object with a model, top to
 * bottom as the tree shows them. A pick that is no longer in the tree, or has
 * lost its model, falls back the same way. Hands back the taxonomy with it, so
 * the panel needs no second query; with counts, since the tree cards show each
 * object's annotations in this project and the active one its summary labels.
 *
 * @param {{ trackCountsFetching?: boolean }} [options] - the pages' Train New Object waits on
 *   `countsFetching`; the panel's tree does not read it, so it is opt-in (see `useObjects`)
 * @returns {{ node: object|null, defaultNode: object|null, nodes: Array, tree: Array,
 *   isLoading: boolean, isError: boolean, countsFetching: boolean }}
 */
export const useAnnotationActiveNode = ({ trackCountsFetching = false } = {}) => {
	const { projectMode, annotationActiveNodeUuid } = useProject();
	const { nodes, tree, isLoading, isError, countsFetching } = useObjects(projectMode, {
		withCounts: true,
		trackCountsFetching,
	});

	const defaultNode = useMemo(() => firstAvailableIn(tree), [tree]);

	const node = useMemo(() => {
		const picked = annotationActiveNodeUuid
			? nodes.find(candidate => candidate.uuid === annotationActiveNodeUuid)
			: null;
		return picked?.is_available ? picked : defaultNode;
	}, [nodes, defaultNode, annotationActiveNodeUuid]);

	return { node, defaultNode, nodes, tree, isLoading, isError, countsFetching };
};
